// overlapGroups.js — customer-facing OVERLAP GROUPING (display only, deterministic)
//
// THE PROBLEM THIS SOLVES:
//   Several independent rules can each flag the SAME underlying physical gap. The
//   clearest case: an estimate missing floor protection trips three rules —
//   OPP008 (reconstruction scope), X002 (trade work over finished floors), and
//   PNT002 (interior painting). Rendered as three separate line items, each with
//   its own dollar, the report reads as padding — one missing item shown three
//   times. Real estimators reject that.
//
// WHAT THIS DOES (and, emphatically, does NOT do):
//   • It groups the co-firing findings so the customer list shows ONE primary
//     finding (the highest-confidence / headline-eligible member) with the others
//     listed underneath as related/contributing — NOT as separate dollar lines.
//   • It is PURE PRESENTATION. It never mutates a finding, never changes a tier,
//     never re-sums or merges any dollar. Every finding keeps its own tier and its
//     own amount; the hero/`value_tiers` totals sum all findings exactly as before.
//     Grouping only decides how the CUSTOMER LIST lays the findings out.
//   • Primary selection is a deterministic total order that puts VERIFIED first, so
//     a Verified finding is ALWAYS the primary wherever one exists — grouping can
//     never move a dollar out of the Verified headline.
//
// THE REGISTRY:
//   Each group is ONE physical gap that multiple rules independently detect. The
//   membership is derived from a cross-audit co-firing analysis over the live
//   corpus (see docs/analysis/overlap-groups-analysis.md). Superficially-similar
//   rules that are actually DISTINCT billables (register-masking vs. exhaust port;
//   handrail vs. baluster vs. stair-finish; install vs. removal) are deliberately
//   NOT grouped — collapsing them would hide legitimate separate opportunities.

export const OVERLAP_GROUPS = [
    {
        key: 'floor_protection',
        label: 'Floor protection',
        // OPP008 reconstruction scope · X002 trade work over finished floors ·
        // PNT002 interior painting — all recommend one floor-protection covering.
        ruleIds: ['XACT-OPP008', 'XACT-X002', 'XACT-PNT002'],
    },
    {
        key: 'pva_primer',
        label: 'PVA primer',
        // C011 after new drywall · OPP003 on drywall reinstall — one coat of PVA
        // primer on the new/reset drywall.
        ruleIds: ['XACT-C011', 'XACT-OPP003'],
    },
    {
        key: 'dust_control_barrier',
        label: 'Dust-control barrier',
        // OPP041 large reconstruction · 2025-DRY-BARR (DRY BARR code) · CM-WTR-BARRP
        // (tension-post containment) — one temporary dust/containment barrier.
        ruleIds: ['XACT-OPP041', 'XACT-2025-DRY-BARR', 'XACT-CM-WTR-BARRP'],
    },
];

// rule_id -> group. Built once at module load.
const RULE_TO_GROUP = new Map();
for (const g of OVERLAP_GROUPS) {
    for (const rid of g.ruleIds) RULE_TO_GROUP.set(rid, g);
}

export function groupForRule(ruleId) {
    return RULE_TO_GROUP.get(ruleId) || null;
}

// Tier rank for primary selection. VERIFIED outranks everything, which is the
// guarantee that a Verified finding is always chosen as the primary — so its
// standalone dollar is preserved and nothing is demoted out of the Verified tier.
const TIER_RANK = { VERIFIED: 3, LIKELY: 2, CONDITIONAL: 1, EXCLUDED: 0 };

function tierRank(f) {
    const t = f && f.commercialOutcome && f.commercialOutcome.tier;
    return TIER_RANK[t] != null ? TIER_RANK[t] : 0;
}
function outcomeScore(f) {
    return (f && f.commercialOutcome && f.commercialOutcome.outcomeScore) || 0;
}
function amountOf(f) {
    const oc = f && f.commercialOutcome;
    if (oc) return (oc.verifiedAmount || 0) || (oc.likelyAmount || 0) || (oc.conditionalAmount || 0) || 0;
    return Number(f && f.dollar_impact) || 0;
}

// Deterministic total order over a group's members. The first element is the
// primary. Order: higher tier (VERIFIED first) → higher outcome score → higher
// amount → rule_id ascending (a stable, data-independent final tiebreak).
function primaryComparator(a, b) {
    return (tierRank(b) - tierRank(a))
        || (outcomeScore(b) - outcomeScore(a))
        || (amountOf(b) - amountOf(a))
        || (a.rule_id < b.rule_id ? -1 : a.rule_id > b.rule_id ? 1 : 0);
}

// assignOverlapRoles(findings) — classify each finding's role within its overlap
// group, for ONE audit's finding list. A group only takes effect when ≥2 of its
// members are actually present (co-firing); a lone member is left untouched.
//
// Returns pure, rule_id-keyed lookups (no finding objects retained, no mutation):
//   roleByRuleId    Map rule_id  -> 'primary' | 'contributing'
//   groupByRuleId   Map rule_id  -> { key, label }
//   primaryOf       Map contribRuleId -> primary rule_id
//   contributorsOf  Map primaryRuleId -> [contributing rule_id, …] (primary order)
//   contributingSet Set of every contributing rule_id (to drop from standalone lists)
export function assignOverlapRoles(findings) {
    const list = Array.isArray(findings) ? findings : [];

    const byGroup = new Map(); // key -> { group, members: [finding] }
    for (const f of list) {
        const g = f && RULE_TO_GROUP.get(f.rule_id);
        if (!g) continue;
        if (!byGroup.has(g.key)) byGroup.set(g.key, { group: g, members: [] });
        byGroup.get(g.key).members.push(f);
    }

    const roleByRuleId = new Map();
    const groupByRuleId = new Map();
    const primaryOf = new Map();
    const contributorsOf = new Map();
    const contributingSet = new Set();

    for (const { group, members } of byGroup.values()) {
        if (members.length < 2) continue; // not co-firing → no grouping
        const sorted = members.slice().sort(primaryComparator);
        const primary = sorted[0];
        const rest = sorted.slice(1);

        roleByRuleId.set(primary.rule_id, 'primary');
        groupByRuleId.set(primary.rule_id, { key: group.key, label: group.label });
        contributorsOf.set(primary.rule_id, rest.map(f => f.rule_id));

        for (const f of rest) {
            roleByRuleId.set(f.rule_id, 'contributing');
            groupByRuleId.set(f.rule_id, { key: group.key, label: group.label });
            primaryOf.set(f.rule_id, primary.rule_id);
            contributingSet.add(f.rule_id);
        }
    }

    return { roleByRuleId, groupByRuleId, primaryOf, contributorsOf, contributingSet };
}
